import { createSlice, PayloadAction, current } from "@reduxjs/toolkit";

interface CartItem {
    id: string;
    name: string;
    price: number;
    quantity: number;
    /** GST % for this service (e.g. 5, 12, 18). Omit to use default in pricing. */
    gstPercent?: number;
}

export interface ColdStorageDetails {
    itemType: string;
    commodityName: string;
    quantity: number;
    quantityUnit: 'kg' | 'ton' | 'crate' | 'box' | 'pallet';
    temperatureType: string;
    temperatureRange: string;
    storageDurationDays: number;
    pickupRequired: boolean;
    deliveryRequired: boolean;
    importExportType: 'domestic' | 'import' | 'export';
    documentsRequired: string[];
    specialInstructions?: string;
}

/**
 * `online` — full amount via PhonePe;
 * `partial_base_fare` — base fare (items subtotal) now, balance at service;
 * `cash_on_service` — full bill in cash at service (no prepayment).
 */
export type PaymentMethod = 'online' | 'partial_base_fare' | 'cash_on_service';

interface CartState {
    cartItems: CartItem[];
    cartTotal: number;
    cartTotalQuantity: number;
    totalPrice: number;
    couponDiscount: number;
    /** Special instructions for the service provider (e.g. gate code, pet alert, call before) */
    orderNotes: string;
    /** Checkout payment choice; offers apply only when `online`. */
    paymentMethod: PaymentMethod;
    /** Catalog service id used for captain assignment on buy & install orders. */
    buyInstallServiceId: string | null;
    /** Structured cold-chain booking data carried into order remarks/metadata. */
    coldStorageDetails: ColdStorageDetails | null;
}

const loadCartState = (): CartState => {
    try {
        const saved = sessionStorage.getItem('redux_cart_state');
        if (saved) {
            return JSON.parse(saved);
        }
    } catch (e) {
        // ignore
    }
    return {
        cartItems: [],
        cartTotal: 0,
        cartTotalQuantity: 0,
        totalPrice: 0,
        couponDiscount: 0,
        orderNotes: '',
        paymentMethod: 'online',
        buyInstallServiceId: null,
        coldStorageDetails: null,
    };
};

const saveCartState = (state: CartState) => {
    try {
        sessionStorage.setItem('redux_cart_state', JSON.stringify(current(state)));
    } catch (e) {
        // ignore
    }
};

const initialState: CartState = loadCartState();

const cartSlice = createSlice({
    name: "cart",
    initialState,
    reducers: {
        addToCart: (state, action: PayloadAction<CartItem>) => {
            const item = state.cartItems.find(i => i.id === action.payload.id);
            if (item) {
                item.quantity += action.payload.quantity;
            } else {
                state.cartItems.push(action.payload);
            }
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        removeFromCart: (state, action: PayloadAction<string>) => {
            state.cartItems = state.cartItems.filter(item => item.id !== action.payload);
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        updateQuantity: (state, action: PayloadAction<{ id: string; quantity: number }>) => {
            const item = state.cartItems.find(i => i.id === action.payload.id);
            if (item) {
                item.quantity = action.payload.quantity;
            }
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        clearCart: (state) => {
            state.cartItems = [];
            state.couponDiscount = 0;
            state.orderNotes = '';
            state.paymentMethod = 'online';
            state.buyInstallServiceId = null;
            state.coldStorageDetails = null;
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        setBuyInstallCart: (
            state,
            action: PayloadAction<{
                items: CartItem[];
                installServiceId: string;
                deliveryNote: string;
            }>,
        ) => {
            state.cartItems = action.payload.items;
            state.buyInstallServiceId = action.payload.installServiceId;
            state.orderNotes = (action.payload.deliveryNote || '').slice(0, 500);
            state.couponDiscount = 0;
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        setColdStorageCart: (
            state,
            action: PayloadAction<{
                item: CartItem;
                details: ColdStorageDetails;
                orderNote: string;
            }>,
        ) => {
            state.cartItems = [action.payload.item];
            state.buyInstallServiceId = null;
            state.coldStorageDetails = action.payload.details;
            state.orderNotes = (action.payload.orderNote || '').slice(0, 500);
            state.couponDiscount = 0;
            cartSlice.caseReducers.calculateTotals(state);
            saveCartState(state);
        },

        setPaymentMethod: (state, action: PayloadAction<PaymentMethod>) => {
            state.paymentMethod = action.payload;
            saveCartState(state);
        },

        setOrderNotes: (state, action: PayloadAction<string>) => {
            state.orderNotes = (action.payload || '').slice(0, 500);
            saveCartState(state);
        },

        setCouponDiscount: (state, action: PayloadAction<number>) => {
            state.couponDiscount = Math.max(0, action.payload);
            saveCartState(state);
        },

        calculateTotals: (state) => {
            state.cartTotal = state.cartItems.reduce((sum, item) => sum + item.quantity, 0);
            state.totalPrice = state.cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0);
            state.cartTotalQuantity = state.cartTotal; // alias
        },
    },
});

export const {
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    setBuyInstallCart,
    setColdStorageCart,
    setOrderNotes,
    setCouponDiscount,
    setPaymentMethod,
    calculateTotals,
} = cartSlice.actions;

export default cartSlice.reducer;
